/**
 * theme.js — Fundraise Platform Shopify Theme
 *
 * Responsibilities:
 *  - Mobile navigation open / close
 *  - Focus trap within open mobile nav
 *  - Keyboard (Escape) and overlay-click dismissal
 *  - aria-expanded / aria-hidden state management
 *  - Body scroll-lock while nav is open
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Helper: query with fallback
  ------------------------------------------------------------------ */
  function $(selector, context) {
    return (context || document).querySelector(selector);
  }

  function $$(selector, context) {
    return Array.from((context || document).querySelectorAll(selector));
  }

  /* ------------------------------------------------------------------
     Helper: all keyboard-focusable elements within a container
  ------------------------------------------------------------------ */
  var FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  function getFocusableElements(container) {
    return $$(FOCUSABLE_SELECTORS, container).filter(function (el) {
      return !el.closest('[hidden]') && !el.closest('[aria-hidden="true"]');
    });
  }

  /* ------------------------------------------------------------------
     Mobile Navigation
  ------------------------------------------------------------------ */
  var MobileNav = {
    hamburger: null,
    nav:       null,
    overlay:   null,
    closeBtn:  null,
    isOpen:    false,
    _previousFocus: null,

    init: function () {
      this.hamburger = $('#hamburger-btn');
      this.nav       = $('#mobile-nav');
      this.overlay   = $('.site-nav-mobile__overlay');
      this.closeBtn  = $('#mobile-nav-close');

      if (!this.hamburger || !this.nav) return; // elements not present on this page

      this.hamburger.addEventListener('click', this.open.bind(this));

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.close.bind(this));
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', this.close.bind(this));
      }

      document.addEventListener('keydown', this._onKeyDown.bind(this));
    },

    open: function () {
      if (this.isOpen) return;
      this.isOpen = true;

      // Remember what had focus so we can restore it on close
      this._previousFocus = document.activeElement;

      // Update ARIA
      this.hamburger.setAttribute('aria-expanded', 'true');
      this.nav.setAttribute('aria-hidden', 'false');
      this.nav.classList.add('is-open');

      if (this.overlay) {
        this.overlay.classList.add('is-visible');
      }

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Move focus to first focusable item inside nav
      var focusable = getFocusableElements(this.nav);
      if (focusable.length) {
        focusable[0].focus();
      }
    },

    close: function () {
      if (!this.isOpen) return;
      this.isOpen = false;

      // Update ARIA
      this.hamburger.setAttribute('aria-expanded', 'false');
      this.nav.setAttribute('aria-hidden', 'true');
      this.nav.classList.remove('is-open');

      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
      }

      // Re-enable body scroll
      document.body.style.overflow = '';

      // Return focus to the element that opened the nav
      if (this._previousFocus) {
        this._previousFocus.focus();
        this._previousFocus = null;
      }
    },

    _onKeyDown: function (event) {
      if (!this.isOpen) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.close();
          break;

        case 'Tab':
          this._trapFocus(event);
          break;
      }
    },

    /** Keep Tab/Shift+Tab cycling within the open nav drawer. */
    _trapFocus: function (event) {
      var focusable = getFocusableElements(this.nav);
      if (!focusable.length) return;

      var first = focusable[0];
      var last  = focusable[focusable.length - 1];

      if (event.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
  };

  /* ------------------------------------------------------------------
     Smooth-scroll for on-page anchor links
     (Respects prefers-reduced-motion; CSS scroll-behavior handles the
     rest in modern browsers, but this polyfills JS-triggered scrolls.)
  ------------------------------------------------------------------ */
  var SmoothScroll = {
    init: function () {
      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) return;

      document.addEventListener('click', function (event) {
        var target = event.target.closest('a[href^="#"]');
        if (!target) return;

        var id = target.getAttribute('href').slice(1);
        if (!id) return;

        var el = document.getElementById(id);
        if (!el) return;

        event.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Update URL without jump
        if (history.pushState) {
          history.pushState(null, '', '#' + id);
        }

        // Move focus to the target element (accessibility)
        if (!el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '-1');
          el.addEventListener('blur', function onBlur() {
            el.removeAttribute('tabindex');
            el.removeEventListener('blur', onBlur);
          });
        }
        el.focus({ preventScroll: true });
      });
    },
  };

  /* ------------------------------------------------------------------
     Sticky header: add `.is-scrolled` class after scrolling 10 px so
     theme can increase shadow / reduce transparency.
  ------------------------------------------------------------------ */
  var StickyHeader = {
    header: null,
    init: function () {
      this.header = $('.site-header');
      if (!this.header) return;

      var self = this;
      var ticking = false;

      window.addEventListener('scroll', function () {
        if (!ticking) {
          window.requestAnimationFrame(function () {
            self._update();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });

      this._update();
    },

    _update: function () {
      if (window.scrollY > 10) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }
    },
  };

  /* ------------------------------------------------------------------
     Scroll Reveal: fade-in journey sections using IntersectionObserver
  ------------------------------------------------------------------ */
  var ScrollReveal = {
    init: function () {
      var targets = $$('.jny-step, .jny-testi, .jny-close, .jny-ts, .fr-stats, .fr-benefits, .fr-programs, .fr-journey, .fr-faq, .pricing-faq, .pricing-model, .pricing-design');

      // Fallback for browsers without IntersectionObserver
      if (!('IntersectionObserver' in window)) {
        $$('.jny-step, .jny-connector, .jny-testi, .jny-close, .jny-ts, .fr-stats, .fr-benefits, .fr-programs, .fr-journey, .fr-faq, .pricing-faq, .pricing-model, .pricing-design, .blog-grid, .article-body, .cf-section').forEach(function (el) {
          el.classList.add('is-visible');
        });
        return;
      }

      if (targets.length) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.22,
          rootMargin: '0px 0px -120px 0px',
        });
        targets.forEach(function (el) { observer.observe(el); });
      }

      // Large full-page content sections: use low threshold so they fire at top-of-section
      var largeSections = $$('.blog-grid, .article-body, .cf-section');
      if (largeSections.length) {
        var largeObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              largeObserver.unobserve(entry.target);
            }
          });
        }, { threshold: 0.05, rootMargin: '0px 0px -60px 0px' });
        largeSections.forEach(function (el) { largeObserver.observe(el); });
      }

      // Connector paths: lighter trigger since elements are short
      var connectors = $$('.jny-connector');
      if (connectors.length) {
        var connectorObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              connectorObserver.unobserve(entry.target);
            }
          });
        }, { threshold: 0.1, rootMargin: '0px 0px -20px 0px' });
        connectors.forEach(function (el) { connectorObserver.observe(el); });
      }
    },
  };

  /* ------------------------------------------------------------------
     FAQ Accordion
  ------------------------------------------------------------------ */
  var FaqAccordion = {
    init: function () {
      var btns = document.querySelectorAll('.fr-faq__btn');
      if (!btns.length) return;
      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var expanded = btn.getAttribute('aria-expanded') === 'true';
          // Collapse all
          btns.forEach(function (b) {
            b.setAttribute('aria-expanded', 'false');
            var answer = document.getElementById(b.getAttribute('aria-controls'));
            if (answer) answer.hidden = true;
          });
          // Open clicked (unless it was already open)
          if (!expanded) {
            btn.setAttribute('aria-expanded', 'true');
            var target = document.getElementById(btn.getAttribute('aria-controls'));
            if (target) target.hidden = false;
          }
        });
      });
    },
  };

  /* ------------------------------------------------------------------
     Bootstrap on DOMContentLoaded
  ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', function () {
    MobileNav.init();
    SmoothScroll.init();
    StickyHeader.init();
    ScrollReveal.init();
    FaqAccordion.init();
  });

})();
